import React, { useCallback, useEffect, useState } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';

/**
 * Bank-transfer payment proofs awaiting finance.
 *
 * Customer service uploads a teller slip against the shipments it covers; this is
 * where finance opens it and decides. Confirming settles the invoices, verifies the
 * shipments and generates their numbers, so the screen leads with the two facts that
 * decision rests on: the slip itself, and claimed against expected.
 *
 * The amount difference is shown, never acted on. A customer who transfers NGN 50,000
 * against NGN 52,000 of freight may have agreed that with their account manager, so
 * finance judges it.
 */

const STATUS_STYLES: Record<string, string> = {
    pending_confirmation: 'bg-amber-100 text-amber-700 border-amber-200',
    confirmed: 'bg-green-100 text-green-700 border-green-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_LABELS: Record<string, string> = {
    pending_confirmation: 'Awaiting finance',
    confirmed: 'Confirmed',
    rejected: 'Rejected',
};

const money = (v: any) => `NGN ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const when = (v: any) => (v ? new Date(v).toLocaleString() : '—');

const apiError = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.message || fallback;

function PaymentProofs() {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [status, setStatus] = useState<string>('pending_confirmation');
    const [search, setSearch] = useState<string>('');

    const [detail, setDetail] = useState<any>(null);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [busy, setBusy] = useState<string>('');

    const [rejecting, setRejecting] = useState<boolean>(false);
    const [reason, setReason] = useState<string>('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await SuperAdminApis.listPaymentProofs({ status, search, per_page: 25 });
            setRows(res?.data?.data ?? []);
        } catch (e) {
            toast.error(apiError(e, 'Could not load payment proofs'));
        } finally {
            setLoading(false);
        }
    }, [status, search]);

    // Only refetches when the filter changes. `load` is deliberately not a dependency:
    // it closes over `search`, so including it would fire a request on every keystroke,
    // which is what the Search button is there to avoid.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const open = async (row: any) => {
        setDetailLoading(true);
        setDetail({ proof: row, shipments: [] });
        try {
            const res = await SuperAdminApis.getPaymentProof(row.id);
            if (res?.success) setDetail(res.data);
        } catch (e) {
            toast.error(apiError(e, 'Could not load this proof'));
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const confirm = async () => {
        const proof = detail?.proof;
        if (!proof) return;

        const diff = Number(proof.amount_difference || 0);
        const warning = diff < 0
            ? `The customer paid ${money(Math.abs(diff))} LESS than these shipments come to.`
            : diff > 0
                ? `The customer paid ${money(diff)} MORE than these shipments come to.`
                : '';

        const ok = window.confirm(
            `Confirm ${proof.reference}?\n\n` +
            `${proof.items_count ?? detail.shipments?.length ?? 0} shipment(s) will be marked paid, verified, and given shipment numbers.\n` +
            (warning ? `\n${warning}\n` : '') +
            `\nThis cannot be undone from here.`
        );
        if (!ok) return;

        setBusy(`confirm-${proof.id}`);
        try {
            const res = await SuperAdminApis.confirmPaymentProof(proof.id);
            if (res?.success) {
                toast.success(res.message);
                const failures = res?.data?.settlement?.shipment_number_failures ?? [];
                // Payment stands even if the partner call fails, so say so plainly
                // rather than letting it look like a clean success.
                if (failures.length) {
                    toast.warn(`${failures.length} shipment number(s) could not be generated. The payment is recorded — retry the numbers from the shipment.`);
                }
                setDetail(null);
                load();
            } else {
                toast.warn(res?.message || 'Could not confirm this proof');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not confirm this proof'));
        } finally {
            setBusy('');
        }
    };

    const reject = async () => {
        const proof = detail?.proof;
        if (!proof) return;
        if (!reason.trim()) { toast.warn('Say why it is being rejected'); return; }

        setBusy(`reject-${proof.id}`);
        try {
            const res = await SuperAdminApis.rejectPaymentProof(proof.id, { reason });
            if (res?.success) {
                toast.success(res.message);
                setRejecting(false);
                setReason('');
                setDetail(null);
                load();
            } else {
                toast.warn(res?.message || 'Could not reject this proof');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not reject this proof'));
        } finally {
            setBusy('');
        }
    };

    const proof = detail?.proof;
    const isPending = proof?.status === 'pending_confirmation';

    return (
        <>
            <AdminSidebar title="Payment Proofs" />
            <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen">
                <AdminNavbar title="Payment Proofs" />

                <div className="lg:px-10 pb-6 px-6">
            <ToastContainer />

            <div className="bg-white rounded-[10px] p-4 mb-4">
                <h2 className="text-[18px] font-semibold mb-1">Payment Proofs</h2>
                <p className="text-sm text-gray-500 mb-4">
                    Bank transfers from cash customers. Confirming settles the invoice, verifies the
                    shipments and generates their shipment numbers.
                </p>

                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Status</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)}
                            className="border rounded-md px-3 py-2 text-sm">
                            <option value="pending_confirmation">Awaiting finance</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="rejected">Rejected</option>
                            <option value="">All</option>
                        </select>
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <label className="block text-xs text-gray-500 mb-1">Search</label>
                        <input value={search} onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                            placeholder="Reference, customer email, teller ref, bank…"
                            className="w-full border rounded-md px-3 py-2 text-sm" />
                    </div>
                    <button onClick={load} disabled={loading}
                        className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60">
                        {loading ? 'Loading…' : 'Search'}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-[10px] p-4">
                {loading ? (
                    <div className="flex justify-center py-10">
                        <Oval height={38} width={38} color="#E04343" secondaryColor="#eee" strokeWidth={4} />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="text-center text-gray-500 py-10">No payment proofs here.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-gray-500 border-b">
                                <tr>
                                    <th className="py-2 px-2">Reference</th>
                                    <th className="py-2 px-2">Customer</th>
                                    <th className="py-2 px-2">Shipments</th>
                                    <th className="py-2 px-2">Claimed</th>
                                    <th className="py-2 px-2">Expected</th>
                                    <th className="py-2 px-2">Submitted by</th>
                                    <th className="py-2 px-2">Status</th>
                                    <th className="py-2 px-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r: any) => (
                                    <tr key={r.id} className="border-b last:border-0">
                                        <td className="py-2 px-2 font-medium">{r.reference}</td>
                                        <td className="py-2 px-2">
                                            <div>{r.customer_name || '—'}</div>
                                            <div className="text-xs text-gray-500">{r.customer_email}</div>
                                        </td>
                                        <td className="py-2 px-2">{r.items_count}</td>
                                        <td className="py-2 px-2">{money(r.amount_claimed)}</td>
                                        <td className="py-2 px-2">
                                            {money(r.amount_expected)}
                                            {!r.amount_matches && (
                                                <span className="block text-xs text-[#E04343] font-medium">
                                                    {Number(r.amount_difference) < 0 ? 'short by ' : 'over by '}
                                                    {money(Math.abs(Number(r.amount_difference)))}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-2">
                                            <div className="text-xs">{r.submitted_by || '—'}</div>
                                            <div className="text-xs text-gray-400">{when(r.submitted_at)}</div>
                                        </td>
                                        <td className="py-2 px-2">
                                            <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_STYLES[r.status] || ''}`}>
                                                {STATUS_LABELS[r.status] || r.status}
                                            </span>
                                            {r.confirmed_by && (
                                                <div className="text-xs text-gray-400 mt-1">by {r.confirmed_by}</div>
                                            )}
                                        </td>
                                        <td className="py-2 px-2">
                                            <button onClick={() => open(r)}
                                                className="text-[#E04343] text-sm font-medium hover:underline">
                                                Review
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
                </div>
            </div>

            {/* Outside the md:ml-64 wrapper on purpose: the overlay is position-fixed
                and must cover the sidebar too, not start to the right of it. */}
            {detail && (
                <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto"
                    onClick={() => { if (!busy) { setDetail(null); setRejecting(false); setReason(''); } }}>
                    <div className="bg-white rounded-[10px] w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b flex items-start justify-between">
                            <div>
                                <h3 className="text-[17px] font-semibold">{proof?.reference}</h3>
                                <p className="text-sm text-gray-500">
                                    {proof?.customer_name || proof?.customer_email}
                                    {proof?.bank_name ? ` · ${proof.bank_name}` : ''}
                                    {proof?.teller_reference ? ` · ref ${proof.teller_reference}` : ''}
                                </p>
                            </div>
                            <button onClick={() => { setDetail(null); setRejecting(false); setReason(''); }}
                                className="text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
                        </div>

                        <div className="p-5">
                            {detailLoading ? (
                                <div className="flex justify-center py-10">
                                    <Oval height={34} width={34} color="#E04343" secondaryColor="#eee" strokeWidth={4} />
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                                        <div className="bg-gray-50 rounded p-3">
                                            <p className="text-xs text-gray-500">Customer says they paid</p>
                                            <p className="text-lg font-semibold">{money(proof?.amount_claimed)}</p>
                                        </div>
                                        <div className="bg-gray-50 rounded p-3">
                                            <p className="text-xs text-gray-500">These shipments come to</p>
                                            <p className="text-lg font-semibold">{money(proof?.amount_expected)}</p>
                                        </div>
                                        <div className={`rounded p-3 ${proof?.amount_matches ? 'bg-green-50' : 'bg-red-50'}`}>
                                            <p className="text-xs text-gray-500">Difference</p>
                                            <p className={`text-lg font-semibold ${proof?.amount_matches ? 'text-green-700' : 'text-[#E04343]'}`}>
                                                {proof?.amount_matches ? 'Matches' : money(proof?.amount_difference)}
                                            </p>
                                        </div>
                                    </div>

                                    {proof?.image_url && (
                                        <div className="mb-5">
                                            <p className="text-xs text-gray-500 mb-1">Proof of payment</p>
                                            {/* Opens full size in a new tab: a teller slip photographed on a
                                                phone is usually unreadable at thumbnail size. */}
                                            <a href={proof.image_url} target="_blank" rel="noreferrer">
                                                <img src={proof.image_url} alt="Payment proof"
                                                    className="max-h-80 rounded border object-contain bg-gray-50" />
                                            </a>
                                            <a href={proof.image_url} target="_blank" rel="noreferrer"
                                                className="block text-xs text-[#E04343] mt-1 hover:underline">
                                                Open full size
                                            </a>
                                        </div>
                                    )}

                                    {proof?.note && (
                                        <p className="text-sm bg-gray-50 rounded p-3 mb-4">
                                            <span className="text-gray-500">Note: </span>{proof.note}
                                        </p>
                                    )}

                                    {proof?.status === 'rejected' && (
                                        <p className="text-sm bg-red-50 text-red-700 rounded p-3 mb-4">
                                            Rejected by {proof.confirmed_by} — {proof.rejection_reason}
                                        </p>
                                    )}

                                    <p className="text-xs text-gray-500 mb-2">
                                        Shipments on this proof ({detail.shipments?.length ?? 0})
                                    </p>
                                    <div className="overflow-x-auto border rounded mb-5 max-h-64 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead className="text-left text-gray-500 bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="py-2 px-2">Shipment No</th>
                                                    <th className="py-2 px-2">Receiver</th>
                                                    <th className="py-2 px-2">State</th>
                                                    <th className="py-2 px-2">Weight</th>
                                                    <th className="py-2 px-2">Status</th>
                                                    <th className="py-2 px-2">Payment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(detail.shipments ?? []).map((s: any) => (
                                                    <tr key={s.id} className="border-t">
                                                        <td className="py-2 px-2">{s.ShipmentNumber || <span className="text-gray-400">not yet issued</span>}</td>
                                                        <td className="py-2 px-2">{s.ReceiverName || '—'}</td>
                                                        <td className="py-2 px-2">{s.ReceiverAddState || '—'}</td>
                                                        <td className="py-2 px-2">{s.Weight ?? '—'}</td>
                                                        <td className="py-2 px-2">{s.Status || '—'}</td>
                                                        <td className="py-2 px-2">{s.payment_status || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {isPending && !rejecting && (
                                        <div className="flex flex-wrap gap-2">
                                            <button onClick={confirm} disabled={!!busy}
                                                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60">
                                                {busy.startsWith('confirm') ? 'Confirming…' : 'Confirm payment'}
                                            </button>
                                            <button onClick={() => setRejecting(true)} disabled={!!busy}
                                                className="bg-white border border-[#E04343] text-[#E04343] px-4 py-2 rounded-md text-sm disabled:opacity-60">
                                                Reject
                                            </button>
                                        </div>
                                    )}

                                    {isPending && rejecting && (
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">
                                                Why is this being rejected? The agent who submitted it will see this.
                                            </label>
                                            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                                                placeholder="e.g. Teller slip is illegible / amount does not match / wrong account"
                                                className="w-full border rounded-md px-3 py-2 text-sm mb-2" />
                                            <div className="flex gap-2">
                                                <button onClick={reject} disabled={!!busy}
                                                    className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm disabled:opacity-60">
                                                    {busy.startsWith('reject') ? 'Rejecting…' : 'Confirm rejection'}
                                                </button>
                                                <button onClick={() => { setRejecting(false); setReason(''); }} disabled={!!busy}
                                                    className="border px-4 py-2 rounded-md text-sm">Cancel</button>
                                            </div>
                                        </div>
                                    )}

                                    {!isPending && (
                                        <p className="text-sm text-gray-500">
                                            {proof?.status === 'confirmed' ? 'Confirmed' : 'Rejected'} by {proof?.confirmed_by || 'unknown'} on {when(proof?.confirmed_at)}.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default PaymentProofs;
